import { useMemo, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { Mail, Send, Users, CheckCircle2, Clock, X, Vote } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import {
  collectPollRespondents,
  normalizeBlogArticle,
  type NewsletterStatus,
  type NewsletterSubscriberSummary,
} from '@jeraisonne/db';
import {
  useNewsletterStats,
  useNewsletterSubscribers,
  useSendNewsletterDigest,
} from '@/lib/admin/use-newsletter';
import { useAdminBlogArticle, useArticlePollVotes } from '@/lib/admin/use-blog';

const inputCls =
  'border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';

const STATUS_BADGE: Record<NewsletterStatus, { label: string; cls: string }> = {
  confirmed: { label: 'Confirmé', cls: 'bg-xp-100 text-xp-700' },
  pending: { label: 'En attente', cls: 'bg-level-50 text-level-600' },
  unsubscribed: { label: 'Désinscrit', cls: 'bg-ink-100 text-ink-500' },
};

function StatusBadge({ status }: { status: NewsletterStatus }) {
  const b = STATUS_BADGE[status] ?? STATUS_BADGE.pending;
  return (
    <span
      className={`font-display shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${b.cls}`}
    >
      {b.label}
    </span>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Users;
  label: string;
  value: number;
  tone: string;
}) {
  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-4">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${tone}`} />
        <span className="font-display text-ink-500 text-[12px] font-semibold uppercase tracking-wide">
          {label}
        </span>
      </div>
      <p className="font-display text-ink-900 mt-1 text-[26px] font-extrabold">{value}</p>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Liste des inscrits ayant répondu au(x) sondage(s) d'un article ──────────

function RespondentsFilter({ articleId, block }: { articleId: string; block?: number }) {
  const { data: rawArticle } = useAdminBlogArticle(articleId);
  const { data: votes = [], isLoading } = useArticlePollVotes(articleId);
  const article = useMemo(
    () => (rawArticle ? normalizeBlogArticle(rawArticle) : null),
    [rawArticle],
  );

  const respondents = useMemo(() => {
    const rows = votes
      .filter((v) => block === undefined || v.block_index === block)
      .map((v) => ({
        block_index: v.block_index,
        choice_index: v.choice_index,
        subscriber: v.subscriber,
      }));
    return collectPollRespondents<NewsletterSubscriberSummary>(rows);
  }, [votes, block]);

  const choiceLabel = (blockIndex: number, choiceIndex: number): string => {
    const choices = article?.blocks[blockIndex]?.poll?.choices;
    return choices?.[choiceIndex] || `Choix ${choiceIndex + 1}`;
  };

  return (
    <section>
      <div className="border-primary-200 bg-primary-50/60 mb-3 flex items-start gap-2 rounded-xl border p-3">
        <Vote className="text-primary-600 mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-display text-ink-900 text-[13px] font-bold">
            Répondants {block !== undefined ? `au sondage ${block + 1}` : 'aux sondages'}
          </p>
          <p className="text-ink-600 truncate font-sans text-[12px]">
            {article ? `« ${article.title} »` : 'Chargement…'}
          </p>
        </div>
        <Link
          to="/tutor/admin/newsletter"
          search={{}}
          className="text-ink-400 hover:text-ink-700 inline-flex shrink-0 items-center gap-1 font-sans text-[12px]"
        >
          <X className="h-3.5 w-3.5" /> Retirer le filtre
        </Link>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <div className="border-primary-500 h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : respondents.length === 0 ? (
        <div className="border-ink-200 text-ink-500 rounded-2xl border bg-white p-6 text-center font-sans text-[13px]">
          Aucun inscrit n&apos;a encore répondu.
        </div>
      ) : (
        <>
          <p className="text-ink-500 mb-2 font-sans text-[12px]">
            {respondents.length} inscrit{respondents.length !== 1 ? 's' : ''}
          </p>
          <div className="border-ink-200 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
            {respondents.map(({ subscriber, answers }) => (
              <div key={subscriber.id} className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-ink-900 min-w-0 truncate font-sans text-[14px]">
                    {subscriber.email}
                  </span>
                  <StatusBadge status={subscriber.status} />
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {answers.map((a) => (
                    <span
                      key={a.blockIndex}
                      className="bg-ink-50 text-ink-600 rounded-md px-1.5 py-0.5 font-sans text-[11px]"
                    >
                      <span className="text-ink-400">S{a.blockIndex + 1} ·</span>{' '}
                      {choiceLabel(a.blockIndex, a.choiceIndex)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// ─── Liste complète des abonnés (vue par défaut) ─────────────────────────────

function AllSubscribers() {
  const { data: subscribers = [], isLoading } = useNewsletterSubscribers();
  return (
    <section>
      <h2 className="font-display text-ink-900 mb-3 text-[15px] font-extrabold">
        Tous les abonnés
      </h2>
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <div className="border-primary-500 h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : subscribers.length === 0 ? (
        <div className="border-ink-200 text-ink-500 rounded-2xl border bg-white p-6 text-center font-sans text-[13px]">
          Aucun abonné pour le moment.
        </div>
      ) : (
        <div className="border-ink-200 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
          {subscribers.map((s) => (
            <div key={s.id} className="flex items-center gap-2 p-3">
              <div className="min-w-0 flex-1">
                <p className="text-ink-900 truncate font-sans text-[14px]">{s.email}</p>
                <p className="text-ink-400 font-sans text-[11px]">
                  Inscrit le {formatDate(s.created_at)}
                  {s.source ? ` · ${s.source}` : ''}
                </p>
              </div>
              <StatusBadge status={s.status} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function TutorAdminNewsletterPage() {
  const search = useSearch({ strict: false }) as { article?: string; block?: number };
  const { data: stats, isLoading } = useNewsletterStats();
  const send = useSendNewsletterDigest();
  const [limit, setLimit] = useState(4);

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <div className="mb-5">
        <h1 className="font-display text-ink-900 flex items-center gap-2 text-[22px] font-extrabold">
          <Mail className="h-5 w-5" />
          Newsletter
        </h1>
        <p className="text-ink-500 mt-0.5 font-sans text-[13px]">
          Abonnés (double opt-in) et envoi du digest des derniers articles.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-3 gap-3">
            <StatCard
              icon={CheckCircle2}
              label="Confirmés"
              value={stats?.confirmed ?? 0}
              tone="text-xp-600"
            />
            <StatCard
              icon={Clock}
              label="En attente"
              value={stats?.pending ?? 0}
              tone="text-level-500"
            />
            <StatCard icon={Users} label="Total" value={stats?.total ?? 0} tone="text-ink-500" />
          </div>

          <div className="border-ink-200 mb-6 rounded-2xl border bg-white p-5">
            <h2 className="font-display text-ink-900 text-[15px] font-extrabold">
              Envoyer le digest
            </h2>
            <p className="text-ink-500 mt-1 font-sans text-[13px]">
              Envoie un email aux <strong>{stats?.confirmed ?? 0}</strong> abonné
              {(stats?.confirmed ?? 0) !== 1 ? 's' : ''} confirmé
              {(stats?.confirmed ?? 0) !== 1 ? 's' : ''} avec les derniers articles publiés (lien de
              désinscription inclus).
            </p>

            <div className="mt-4 flex flex-col gap-1.5">
              <label className="font-display text-ink-700 text-[12px] font-semibold">
                Nombre d&apos;articles à inclure
              </label>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className={inputCls}
              >
                {[2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n} articles
                  </option>
                ))}
              </select>
            </div>

            <Button
              className="mt-4 flex items-center gap-1.5"
              disabled={send.isPending || (stats?.confirmed ?? 0) === 0}
              onClick={() => send.mutate({ limit })}
            >
              <Send className="h-4 w-4" />
              {send.isPending ? 'Envoi…' : 'Envoyer le digest'}
            </Button>

            {send.isSuccess && send.data && (
              <p className="text-xp-700 mt-3 font-sans text-[13px]">
                ✓ {send.data.sent} email{send.data.sent !== 1 ? 's' : ''} envoyé
                {send.data.sent !== 1 ? 's' : ''}
                {send.data.failed > 0 ? ` (${send.data.failed} échec(s))` : ''} —{' '}
                {send.data.articles} article{send.data.articles !== 1 ? 's' : ''}.
              </p>
            )}
            {send.isError && (
              <p className="text-accent-600 mt-3 font-sans text-[13px]">{send.error?.message}</p>
            )}
          </div>

          {search.article ? (
            <RespondentsFilter articleId={search.article} block={search.block} />
          ) : (
            <AllSubscribers />
          )}
        </>
      )}
    </div>
  );
}
